<template>
  <!--
    Componente "headless" — no renderiza Vue, gestiona un THREE.Group inyectado
    en el Viewer Three.js que recibe por ref. Vive como hijo invisible.
  -->
  <div style="display: none"></div>
</template>

<script>
import * as THREE from 'three'

/**
 * Overlay de diff sobre el viewer Three.js de Pictogramas.
 *
 * Pinta tres tipos de marcadores sobre el render del cadJson candidato:
 *   - added:   rect verde semi-transparente en la posición nueva
 *   - removed: rect rojo semi-transparente en su posición original
 *              (el viewer no las dibuja por sí mismo porque cargó el candidato)
 *   - moved:   rect ámbar en la posición destino + flecha desde origen
 *
 * Las visibilidades se controlan con props `showAdded`/`showRemoved`/`showMoved`.
 * Al destruirse el componente o cambiar el diff/viewer, todo lo agregado al
 * scene se limpia.
 */
export default {
  name: 'DxfDiffOverlay',

  props: {
    /** ref al componente Viewer.vue (no la instancia DxfViewer interna). */
    viewer:      { type: Object, default: null },
    /** Objeto del backend: { added: [], removed: [], moved: [] }. */
    diff:        { type: Object, default: () => ({ added: [], removed: [], moved: [] }) },
    showAdded:   { type: Boolean, default: true },
    showRemoved: { type: Boolean, default: true },
    showMoved:   { type: Boolean, default: true },
  },

  data() {
    return {
      addedGroup:   null,
      removedGroup: null,
      movedGroup:   null,
    }
  },

  watch: {
    viewer:      { handler: '_Rebuild', immediate: false },
    diff:        { handler: '_Rebuild', deep: true },
    showAdded()  { if (this.addedGroup)   this.addedGroup.visible   = this.showAdded },
    showRemoved(){ if (this.removedGroup) this.removedGroup.visible = this.showRemoved },
    showMoved()  { if (this.movedGroup)   this.movedGroup.visible   = this.showMoved },
  },

  mounted() {
    // El viewer puede no estar listo todavía. Reintentamos hasta encontrarlo.
    this.$nextTick(() => this._Rebuild())
  },

  beforeDestroy() {
    this._Cleanup()
  },

  methods: {
    _GetDxfViewer() {
      const dxf = this.viewer?.GetDxfViewer?.()
      if (!dxf?.HasRenderer?.()) return null
      return dxf
    },

    _Cleanup() {
      const dxf = this._GetDxfViewer()
      const scene = dxf?.GetScene?.()
      if (scene) {
        for (const g of [this.addedGroup, this.removedGroup, this.movedGroup]) {
          if (g) {
            scene.remove(g)
            this._DisposeGroup(g)
          }
        }
      }
      this.addedGroup = null
      this.removedGroup = null
      this.movedGroup = null
    },

    /** Libera geometrías + materiales del grupo recursivamente. */
    _DisposeGroup(group) {
      group.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.()
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose?.())
          else obj.material.dispose?.()
        }
      })
    },

    _Rebuild() {
      this._Cleanup()

      const dxf = this._GetDxfViewer()
      if (!dxf) return

      const scene = dxf.GetScene()
      const origin = dxf.GetOrigin() || { x: 0, y: 0 }
      if (!scene) return

      const d = this.diff || {}

      // ---- added (verde) ----
      this.addedGroup = this._BuildRectGroup(
        d.added || [],
        origin,
        0x22c55e,  // green-500
        0.35,
      )
      this.addedGroup.visible = this.showAdded
      scene.add(this.addedGroup)

      // ---- removed (rojo) ----
      // Para removed, las coords están en el JSON original — el backend ya las
      // mandó en el shape normalize() (x, y, width, height).
      this.removedGroup = this._BuildRectGroup(
        d.removed || [],
        origin,
        0xef4444,  // red-500
        0.35,
      )
      this.removedGroup.visible = this.showRemoved
      scene.add(this.removedGroup)

      // ---- moved (ámbar + flechas) ----
      this.movedGroup = this._BuildMovedGroup(d.moved || [], origin)
      this.movedGroup.visible = this.showMoved
      scene.add(this.movedGroup)

      // Forzar repaint
      dxf.Render?.()
    },

    /**
     * Construye un Group con un rect semi-transparente por cada entry.
     * Cada entry tiene x, y, width, height — coordenadas del DXF originales
     * que hay que ofsetear por origin para alinearse con el scene.
     */
    _BuildRectGroup(entries, origin, color, opacity) {
      const group = new THREE.Group()
      for (const entry of entries) {
        const x = Number(entry.x)
        const y = Number(entry.y)
        const w = Number(entry.width  ?? 0)
        const h = Number(entry.height ?? 0)
        if (!isFinite(x) || !isFinite(y)) continue
        if (w <= 0 || h <= 0) {
          // Sin dimensiones útiles → marcador circular pequeño
          group.add(this._MakeCircle(x - origin.x, y - origin.y, 1.5, color, opacity))
          continue
        }
        group.add(this._MakeRect(x - origin.x, y - origin.y, w, h, color, opacity))
      }
      return group
    },

    _BuildMovedGroup(entries, origin) {
      const group = new THREE.Group()
      const color = 0xf59e0b  // amber-500
      for (const entry of entries) {
        const to   = entry.to
        const from = entry.from
        if (!to || !from) continue

        const w = Number(entry.width  ?? 0)
        const h = Number(entry.height ?? 0)

        // Rect destino sólido (semi-transparente)
        if (w > 0 && h > 0) {
          group.add(this._MakeRect(to.x - origin.x, to.y - origin.y, w, h, color, 0.35))
        } else {
          group.add(this._MakeCircle(to.x - origin.x, to.y - origin.y, 1.5, color, 0.35))
        }

        // Flecha desde origen hasta destino (línea simple — Three.Line)
        group.add(this._MakeArrow(
          from.x - origin.x, from.y - origin.y,
          to.x   - origin.x, to.y   - origin.y,
          color,
        ))
      }
      return group
    },

    _MakeRect(x, y, w, h, color, opacity) {
      const geometry = new THREE.PlaneGeometry(w, h)
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthTest:   false,    // pintar siempre encima del scene
        depthWrite:  false,
        side:        THREE.DoubleSide,
      })
      const mesh = new THREE.Mesh(geometry, material)
      // El centro del PlaneGeometry está en (0,0). Ofseteamos al centro del rect.
      mesh.position.set(x + w / 2, y + h / 2, 0.1)
      mesh.renderOrder = 999   // por encima de todo

      // Borde (line loop alrededor del rect)
      const edges = new THREE.EdgesGeometry(geometry)
      const lineMat = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
      })
      const border = new THREE.LineSegments(edges, lineMat)
      border.position.copy(mesh.position)
      border.renderOrder = 1000

      const group = new THREE.Group()
      group.add(mesh)
      group.add(border)
      return group
    },

    _MakeCircle(x, y, radius, color, opacity) {
      const geometry = new THREE.CircleGeometry(radius, 16)
      const material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthTest:   false,
        depthWrite:  false,
      })
      const mesh = new THREE.Mesh(geometry, material)
      mesh.position.set(x, y, 0.1)
      mesh.renderOrder = 999
      return mesh
    },

    _MakeArrow(x1, y1, x2, y2, color) {
      const points = [
        new THREE.Vector3(x1, y1, 0.2),
        new THREE.Vector3(x2, y2, 0.2),
      ]
      const geometry = new THREE.BufferGeometry().setFromPoints(points)
      const material = new THREE.LineBasicMaterial({
        color,
        linewidth: 2,
        depthTest: false,
      })
      const line = new THREE.Line(geometry, material)
      line.renderOrder = 1001

      // Cabeza de flecha en el destino (triángulo pequeño orientado)
      const dx = x2 - x1
      const dy = y2 - y1
      const len = Math.sqrt(dx * dx + dy * dy)
      const group = new THREE.Group()
      group.add(line)
      if (len > 0) {
        const headSize = Math.min(len * 0.15, 1.5)
        const ux = dx / len
        const uy = dy / len
        // Perpendicular
        const px = -uy
        const py = ux
        const tip   = new THREE.Vector3(x2, y2, 0.2)
        const baseA = new THREE.Vector3(x2 - ux * headSize + px * headSize * 0.5, y2 - uy * headSize + py * headSize * 0.5, 0.2)
        const baseB = new THREE.Vector3(x2 - ux * headSize - px * headSize * 0.5, y2 - uy * headSize - py * headSize * 0.5, 0.2)
        const headGeom = new THREE.BufferGeometry().setFromPoints([tip, baseA, baseB, tip])
        const head = new THREE.Line(headGeom, material)
        head.renderOrder = 1001
        group.add(head)
      }
      return group
    },
  },
}
</script>
